import * as React from 'react';
import { resolveThemeBrand } from '@authe/fe-commons/theme';
import { jsx } from 'react/jsx-runtime';

// src/theme/ThemeProvider.tsx
var ThemeContext = React.createContext(null);
var EMPTY_BRAND = {};
function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
function useOptionalTheme() {
  return React.useContext(ThemeContext);
}
var SYSTEM_THEME_QUERY = "(prefers-color-scheme: dark)";
function subscribeSystemTheme(onChange) {
  if (typeof window.matchMedia !== "function") return () => {
  };
  const media = window.matchMedia(SYSTEM_THEME_QUERY);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
var systemIsDark = () => typeof window.matchMedia === "function" && window.matchMedia(SYSTEM_THEME_QUERY).matches;
var serverIsDark = () => false;
function readStored(storageKey, defaultMode) {
  if (typeof window === "undefined") return defaultMode;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
  }
  return defaultMode;
}
function ThemeProvider({
  children,
  storageKey = "authe-theme",
  defaultMode = "system",
  defaultBrand = EMPTY_BRAND
}) {
  const [mode, setModeState] = React.useState(() => readStored(storageKey, defaultMode));
  const [brandOverrides, setBrand] = React.useState(EMPTY_BRAND);
  const brand = React.useMemo(() => ({ ...defaultBrand, ...brandOverrides }), [defaultBrand, brandOverrides]);
  const darkSystem = React.useSyncExternalStore(subscribeSystemTheme, systemIsDark, serverIsDark);
  const resolvedMode = mode === "system" ? darkSystem ? "dark" : "light" : mode;
  React.useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const previous = root.classList.contains("dark");
    root.classList.toggle("dark", resolvedMode === "dark");
    return () => {
      root.classList.toggle("dark", previous);
    };
  }, [resolvedMode]);
  React.useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const resolved = resolveThemeBrand(brand, resolvedMode);
    const properties = { "--brand": resolved.brand, "--brand-foreground": resolved.brandForeground, "--font-sans": resolved.fontSans };
    const previous = Object.keys(properties).map((name) => [name, root.style.getPropertyValue(name), root.style.getPropertyPriority(name)]);
    for (const [name, value2] of Object.entries(properties)) {
      if (value2) root.style.setProperty(name, value2);
      else root.style.removeProperty(name);
    }
    return () => {
      for (const [name, value2, priority] of previous) {
        if (value2) root.style.setProperty(name, value2, priority);
        else root.style.removeProperty(name);
      }
    };
  }, [brand, resolvedMode]);
  const setMode = React.useCallback(
    (next) => {
      setModeState(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(storageKey, next);
        } catch {
        }
      }
    },
    [storageKey]
  );
  const value = React.useMemo(
    () => ({ mode, setMode, resolvedMode, brand, setBrand }),
    [mode, setMode, resolvedMode, brand]
  );
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value, children });
}

export { ThemeProvider, useOptionalTheme, useTheme };
//# sourceMappingURL=chunk-NH2JFPA3.js.map
//# sourceMappingURL=chunk-NH2JFPA3.js.map